// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerKeyJoinDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action } from 'mobx';
import {
  OBJECT_STATE,
  OPERATOR_TYPES,
  DEFAULT_COMPARISION_OPERATOR,
  EMPTY_GUID
} from 'logic/data/Izenda.ReportDesignerConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import { isEmpty, get, getOr, uniqueId, clone as _clone, isNil } from 'lodash/fp';

@tracker
class ReportDesignerKeyJoinDetail {
  @observable parentId;
  @observable isKeyJoin;
  @observable relationshipId;
  @observable joinFieldId;
  @observable foreignFieldId;
  @observable operator;
  @observable joinQuerySourceId;
  @observable joinQuerySourceName;
  @observable joinDataSourceCategoryId;
  @observable joinDataSourceCategoryName;
  @observable joinFieldName;
  @observable foreignFieldName;
  @observable foreignQuerySourceId;
  @observable foreignQuerySourceName;
  @observable foreignDataSourceCategoryId;
  @observable foreignDataSourceCategoryName;
  @observable joinSourceAlias;
  @observable foreignSourceAlias;
  @observable selectedJoinSourceAlias;
  @observable selectedForeignSourceAlias;
  @observable id;
  @observable state;
  @observable modified;
  @observable dateTimeNow;
  @observable validationKey;
  @observable position;
  @observable level;
  @observable comparisonOperator;
  @observable comparisonValue;
  @observable isForeignDataObjectAlias;

  constructor(detail: any) {
    this.tempId = '';
    this.convertedFromRelationshipId = '';
    this.populateData(detail || {});
    this.resetDirty();
  }

  @computed
  get deleted() {
    return this.state === OBJECT_STATE.DELETE;
  }

  @computed
  get isJoinQuerySourceReady() {
    return !isEmpty(this.joinDataSourceCategoryId);
  }

  @computed
  get isforeignQuerySourceReady() {
    return !isEmpty(this.foreignDataSourceCategoryId);
  }

  @computed
  get isJoinFieldReady() {
    return !isEmpty(this.selectedJoinSourceAlias);
  }

  @computed
  get isForeignFieldReady() {
    return !isEmpty(this.selectedForeignSourceAlias);
  }

  @computed
  get originalAliasId() {
    if (this.selectedForeignSourceAlias && this.selectedForeignSourceAlias.indexOf('_') >= 0) {
      const underscoreIndex = this.selectedForeignSourceAlias.indexOf('_');
      return this.selectedForeignSourceAlias.substring(0, underscoreIndex);
    }
    return this.selectedForeignSourceAlias;
  }

  /**
   * Populate data for key join
   * (This is intended to populating which is returned from API.)
   * @param {any} detail
   */
  @action('populateData')
  populateData(detail: any) {
    this.tempId = get('id', detail) || uniqueId('keyjoin_');
    this.parentId = getOr(null, 'relationshipId', detail);
    this.isKeyJoin = true;
    this.relationshipId = getOr(null, 'relationshipId', detail);
    this.joinFieldId = getOr(null, 'joinFieldId', detail);
    this.foreignFieldId = getOr(null, 'foreignFieldId', detail);
    this.operator = get('operator', detail) || OPERATOR_TYPES[0].label;
    this.joinQuerySourceId = getOr(null, 'joinQuerySourceId', detail);
    this.joinQuerySourceName = get('joinQuerySourceName', detail) || '';
    this.joinDataSourceCategoryId = getOr(null, 'joinDataSourceCategoryId', detail);
    this.joinDataSourceCategoryName = get('joinDataSourceCategoryName', detail) || '';
    this.joinFieldName = get('joinFieldName', detail) || '';
    this.foreignFieldName = get('foreignFieldName', detail) || '';
    this.foreignQuerySourceId = getOr(null, 'foreignQuerySourceId', detail);
    this.foreignQuerySourceName = get('foreignQuerySourceName', detail) || '';
    this.foreignDataSourceCategoryId = getOr(null, 'foreignDataSourceCategoryId', detail);
    this.foreignDataSourceCategoryName = get('foreignDataSourceCategoryName', detail) || '';
    this.joinSourceAlias = get('joinSourceAlias', detail) || '';
    this.foreignSourceAlias = get('foreignSourceAlias', detail) || '';
    this.selectedJoinSourceAlias = get('selectedJoinSourceAlias', detail) || '';
    this.selectedForeignSourceAlias = get('selectedForeignSourceAlias', detail) || '';
    this.id = getOr(null, 'id', detail);
    (this.state = !isNil(detail.state) ? detail.state : OBJECT_STATE.INSERT), (this.modified = null);
    this.dateTimeNow = null;
    this.validationKey = get('id', detail) || this.tempId;
    this.position = get('position', detail) || 0;
    this.level = null;
    this.comparisonOperator = get('comparisonOperator', detail) || DEFAULT_COMPARISION_OPERATOR;
    this.comparisonValue = getOr(null, 'comparisonValue', detail);
    // This flag is use to mark the foreign data object of this relationship use alias
    this.isForeignDataObjectAlias = get('isForeignDataObjectAlias', detail) || false;
    this.convertedFromRelationshipId = get('convertedFromRelationshipId', detail);
  }

  /**
   * Update join data source category.
   * @param {string} id
   * @param {string} name
   */
  @action('setJoinDataSourceCategory')
  setJoinDataSourceCategory(id: string, name: string) {
    this.joinDataSourceCategoryId = id;
    this.joinDataSourceCategoryName = name;
    this.setJoinQuerySource(null);
    this.updateState();
  }

  /**
   * Update foreign data source category.
   * @param {string} id
   * @param {string} name
   */
  @action('setForeignDataSourceCategory')
  setForeignDataSourceCategory(id: string, name: string) {
    this.foreignDataSourceCategoryId = id;
    this.foreignDataSourceCategoryName = name;
    this.setForeignQuerySource(null);
    this.updateState();
  }

  /**
   * Update join query source.
   * @param {string} id
   * @param {string} name
   */
  @action('setJoinQuerySource')
  setJoinQuerySource(id: string, name: string) {
    this.selectedJoinSourceAlias = id;
    this.joinQuerySourceName = name;
    this.joinFieldId = null;
    this.updateState();
  }

  /**
   * Update foreign query source.
   * @param {string} id
   * @param {string} name
   * @param {boolean} isDataSourceAlias
   */
  @action('setForeignQuerySource')
  setForeignQuerySource(id: string, name: string, isDataSourceAlias: boolean) {
    this.isForeignDataObjectAlias = !!isDataSourceAlias;
    this.selectedForeignSourceAlias = id;
    this.foreignQuerySourceName = name;
    this.foreignFieldId = null;
    this.updateState();
  }

  /**
   * Set primary for join field
   * @param  {string} id
   * @param  {string} name
   */
  @action('setJoinField')
  setJoinField(id: string, name: string) {
    this.joinFieldId = id;
    this.joinFieldName = name;
    this.updateState();
  }

  /**
   * Set primary for foreign field
   * @param  {string} id
   * @param  {string} name
   */
  @action('setForeignField')
  setForeignField(id: string, name: string) {
    this.foreignFieldId = id;
    this.foreignFieldName = name;
    this.updateState();
  }

  /**
   * Clone a new KeyJoinDetail with new Id
   */
  @action('clone')
  clone() {
    const cloneObj = _clone(this);
    const keyJoin = new ReportDesignerKeyJoinDetail(cloneObj);
    keyJoin.setParentId(cloneObj.parentId);
    keyJoin.newId();
    return keyJoin;
  }

  /**
   * Set delete status.
   */
  delete() {
    this.state = OBJECT_STATE.DELETE;
  }

  /**
   * Set alias
   * @param {any} value
   */
  @action('setAlias')
  setAlias(value: any) {
    this.alias = value;
    this.updateState();
  }

  /**
   * Set state
   * @param {number} state
   */
  setState(state: number) {
    this.state = state;
  }

  /**
   * Update state
   */
  updateState() {
    // just update state to UPDATE when editing existing item
    // do not update deleted item's state
    this.id && this.state !== OBJECT_STATE.DELETE && this.setState(OBJECT_STATE.UPDATE);
  }

  /**
   * Update hidden status
   * @param {boolean} hidden
   */
  setHidden(hidden: boolean) {
    this.hidden = hidden;
  }

  /**
   * Update key join item value.
   * @param {string} key
   * @param {any} value
   */
  @action('updateKeyJoinItem')
  updateKeyJoinItem(key: string, value: any) {
    this[key] = value;
    this.updateState();
  }

  /**
   * Get id of key join
   */
  getId() {
    return this.id || this.tempId;
  }

  /**
   * Build and update new id value.
   */
  @action('newId')
  newId() {
    this.id = null;
    this.tempId = uniqueId('keyjoin_');
  }

  /**
   * Update parent id.
   * @param {string} parentId
   */
  setParentId(parentId: string) {
    this.parentId = parentId;
  }

  /**
   * Set Comparison Operator
   * @param {any} value
   */
  @action('setComparisionOperator')
  setComparisionOperator(value: any) {
    this.comparisonOperator = value;
    this.markDirty();
    this.updateState();
  }

  /**
   * Set Comparison Value
   * @param {any} value
   */
  @action('setComparisionValue')
  setComparisionValue(value: any) {
    this.comparisonValue = value;
    this.markDirty();
    this.updateState();
  }

  /**
   * Update Foreign Data Object Alias
   * @param {string} originAlias
   * @param {string} newAlias
   */
  @action('updateForeignDataObjectAlias')
  updateForeignDataObjectAlias(originAlias: string, newAlias: string) {
    if (this.isForeignDataObjectAlias && this.selectedForeignSourceAlias === `${this.originalAliasId}_${originAlias}`) {
      this.selectedForeignSourceAlias = `${this.originalAliasId}_${newAlias}`;
    }
  }

  /**
   * Update Data Object alias and Foreign Data Object alias
   * @param {string} joinQuerySourceId
   * @param {string} originAlias
   * @param {string} newAlias
   */
  @action
  updateAlias(joinQuerySourceId: string, originAlias: string, newAlias: string) {
    const originAliasString = this.createAliasString(joinQuerySourceId, originAlias);
    const newAliasString = this.createAliasString(joinQuerySourceId, newAlias);
    if (this.isJoinFieldReady && this.selectedJoinSourceAlias === originAliasString) {
      this.selectedJoinSourceAlias = newAliasString;
    }

    if (this.isForeignFieldReady && this.selectedForeignSourceAlias === originAliasString) {
      this.selectedForeignSourceAlias = newAliasString;
    }
  }

  /**
   * Create a string template for alias
   * @param {string} sourceId
   * @param {string} alias
   */
  createAliasString(sourceId: string, alias: string) {
    return `${sourceId}_${alias}`;
  }
}

export default ReportDesignerKeyJoinDetail;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerKeyJoinDetail.js